import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { detectLanguage } from '../../../utils/detectLanguage';

const Footer = ({ language }) => {
  const location = useLocation();
  const currentYear = new Date()?.getFullYear();

  // Detect current language from URL if not provided, using safe utility
  const currentLang = language || detectLanguage(location?.pathname);

  const footerContent = {
    en: {
      copyright: `© ${currentYear} FinancialCalculatorNetwork. All rights reserved.`,
      languagesTitle: 'Languages',
      toolsTitle: 'Calculators',
      tools: [
        { label: 'Debt Freedom Calculator', path: '/debt-freedom-calculator' },
        { label: 'Money Duration Calculator', path: '/money-duration-calculator' }
      ],
      links: [
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Contact', path: '/contact' }
      ]
    },
    es: {
      copyright: `© ${currentYear} FinancialCalculatorNetwork. Todos los derechos reservados.`,
      languagesTitle: 'Idiomas',
      toolsTitle: 'Calculadoras',
      tools: [
        { label: 'Calculadora de Libertad de Deudas', path: '/es/debt-freedom-calculator' },
        { label: 'Calculadora de Duración del Dinero', path: '/es/money-duration-calculator' }
      ],
      links: [
        { label: 'Política de Privacidad', path: '/privacy' },
        { label: 'Términos de Servicio', path: '/terms' },
        { label: 'Contacto', path: '/contact' }
      ]
    },
    pt: {
      copyright: `© ${currentYear} FinancialCalculatorNetwork. Todos os direitos reservados.`,
      languagesTitle: 'Idiomas',
      toolsTitle: 'Calculadoras',
      tools: [
        { label: 'Calculadora de Liberdade de Dívidas', path: '/pt/debt-freedom-calculator' },
        { label: 'Calculadora de Duração do Dinheiro', path: '/pt/money-duration-calculator' }
      ],
      links: [
        { label: 'Política de Privacidade', path: '/privacy' },
        { label: 'Termos de Serviço', path: '/terms' },
        { label: 'Contato', path: '/contact' }
      ]
    },
    fr: {
      copyright: `© ${currentYear} FinancialCalculatorNetwork. Tous droits réservés.`,
      languagesTitle: 'Langues',
      toolsTitle: 'Calculatrices',
      tools: [
        { label: 'Calculateur de Liberté de Dette', path: '/fr/debt-freedom-calculator' },
        { label: 'Calculateur de Durée d\'Argent', path: '/fr/money-duration-calculator' }
      ],
      links: [
        { label: 'Politique de Confidentialité', path: '/privacy' },
        { label: 'Conditions de Service', path: '/terms' },
        { label: 'Contact', path: '/contact' }
      ]
    },
    de: {
      copyright: `© ${currentYear} FinancialCalculatorNetwork. Alle Rechte vorbehalten.`,
      languagesTitle: 'Sprachen',
      toolsTitle: 'Rechner',
      tools: [
        { label: 'Schuldenfreiheitsrechner', path: '/de/debt-freedom-calculator' },
        { label: 'Gelddauerrechner', path: '/de/money-duration-calculator' }
      ],
      links: [
        { label: 'Datenschutzrichtlinie', path: '/privacy' },
        { label: 'Nutzungsbedingungen', path: '/terms' },
        { label: 'Kontakt', path: '/contact' }
      ]
    }
  };

  const languages = [
    { code: 'en', name: 'English', path: '/' },
    { code: 'es', name: 'Español', path: '/es' },
    { code: 'pt', name: 'Português', path: '/pt' },
    { code: 'fr', name: 'Français', path: '/fr' },
    { code: 'de', name: 'Deutsch', path: '/de' }
  ];

  const content = footerContent?.[currentLang] || footerContent?.en;

  return (
    <footer className="bg-card border-t border-border mt-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Languages Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {content?.languagesTitle}
            </h3>
            <nav className="flex flex-col gap-2">
              {languages?.map((lang) => (
                <Link
                  key={lang?.code}
                  to={lang?.path}
                  className={`text-sm transition-smooth ${
                    currentLang === lang?.code
                      ? 'text-primary font-medium' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {lang?.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Tools/Calculators Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">
              {content?.toolsTitle}
            </h3>
            <nav className="flex flex-col gap-2">
              {content?.tools?.map((tool, index) => (
                <Link
                  key={index}
                  to={tool?.path}
                  className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
                >
                  {tool?.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Legal Links Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Legal
            </h3>
            <nav className="flex flex-col gap-2">
              {content?.links?.map((link, index) => (
                <Link
                  key={index}
                  to={link?.path}
                  className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
                >
                  {link?.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Calculator" size={18} color="var(--color-primary)" />
            </div>
            <span className="text-sm text-muted-foreground">
              {content?.copyright}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;